import {
	getFormIdFromElement,
	isNextSubmissionType,
	isPreviousSubmissionType,
} from '../shared/gf-submission.js';
import { REQUESTED_TARGET_INPUT_NAME_PREFIX } from './sidebar-constants.js';
import {
	getSidebarApp,
	readRequestedTargetPage as readRequestedTargetFromStore,
	resolveMaxVisitedPage,
	writeRequestedTargetPage as writeRequestedTargetToStore,
} from './sidebar-runtime-store.js';
import {
	resolveCurrentPage,
	resolveVisibleStepNumbers,
} from './sidebar-step-state.js';
import {
	findPageElement,
	getForm,
	escapeAttributeValue,
	toPositiveInteger,
} from './sidebar-utils.js';

const registeredPreSubmissionFilterForms = new Set();

/**
* Responsibility:
* - Build the custom hidden-field name used to persist sidebar target jumps through GF submission.
*
* Touches:
* - Used by requested-target input lookup and creation helpers.
*
* Guarantees:
* - Returns a deterministic field name for one positive form id.
*/
function getRequestedTargetInputName(formId) {
	return `${REQUESTED_TARGET_INPUT_NAME_PREFIX}${toPositiveInteger(formId)}`;
}

/**
* Responsibility:
* - Resolve the custom hidden input that carries sidebar-requested target pages.
*
* Touches:
* - Queries the current form by the deterministic requested-target field name.
* - Optionally creates the hidden input in the current form when missing.
*
* Guarantees:
* - Returns the hidden input when it exists or can be created, otherwise `null`.
*/
function getRequestedTargetInput(form, formId, { create = false } = {}) {
	if (!(form instanceof HTMLElement) || formId <= 0) {
		return null;
	}

	const fieldName = getRequestedTargetInputName(formId);
	const escapedFieldName = escapeAttributeValue(fieldName);
	const existingInput = form.querySelector(`input[type="hidden"][name="${escapedFieldName}"]`);
	if (existingInput instanceof HTMLInputElement) {
		return existingInput;
	}

	if (!create) {
		return null;
	}

	const requestedTargetInput = document.createElement('input');
	requestedTargetInput.type = 'hidden';
	requestedTargetInput.name = fieldName;
	form.appendChild(requestedTargetInput);

	return requestedTargetInput;
}

/**
* Responsibility:
* - Read the pending sidebar-requested GF target page from explicit runtime state.
*
* Touches:
* - Reads module-level runtime state first, then falls back to the hidden submission field.
*
* Guarantees:
* - Returns a positive target page or `0`.
*/
function readRequestedTargetPage(form) {
	if (!(form instanceof HTMLElement)) {
		return 0;
	}

	const formId = getFormIdFromElement(form);
	const requestedTargetPage = readRequestedTargetFromStore(formId);
	if (requestedTargetPage > 0) {
		return requestedTargetPage;
	}

	if (formId <= 0) {
		return 0;
	}

	const requestedTargetInput = getRequestedTargetInput(form, formId);
	return requestedTargetInput instanceof HTMLInputElement
		? toPositiveInteger(requestedTargetInput.value)
		: 0;
}

/**
* Responsibility:
* - Persist the pending sidebar-requested GF target page for the current submission cycle.
*
* Touches:
* - Writes module-level runtime state and mirrors it into the hidden submission field.
*
* Guarantees:
* - Stores only a positive target page or `0`.
*/
function writeRequestedTargetPage(form, targetPage) {
	if (!(form instanceof HTMLElement)) {
		return;
	}

	const formId = getFormIdFromElement(form);
	const normalizedTargetPage = toPositiveInteger(targetPage);
	writeRequestedTargetToStore(formId, normalizedTargetPage);

	if (formId <= 0) {
		return;
	}

	const requestedTargetInput = getRequestedTargetInput(form, formId, {
		create: true,
	});
	if (requestedTargetInput instanceof HTMLInputElement) {
		requestedTargetInput.value = normalizedTargetPage > 0 ? String(normalizedTargetPage) : '';
	}
}

/**
* Responsibility:
* - Clear any pending sidebar-requested GF target page.
*
* Touches:
* - Resets explicit runtime state and the mirrored hidden submission field.
*
* Guarantees:
* - Leaves the form with no pending requested page jump.
*/
export function clearRequestedTargetPage(form) {
	writeRequestedTargetPage(form, 0);
}

/**
* Responsibility:
* - Resolve the native GF target-page hidden input for the current form.
*
* Touches:
* - Queries `gform_target_page_number_{formId}` by its native GF input name.
*
* Guarantees:
* - Returns existing target-page input or `null` when unavailable.
*/
function getNativeTargetPageInput(form, formId) {
	if (!(form instanceof HTMLElement) || formId <= 0) {
		return null;
	}

	const fieldName = `gform_target_page_number_${formId}`;
	const escapedFieldName = escapeAttributeValue(fieldName);
	const input = form.querySelector(`input[type="hidden"][name="${escapedFieldName}"]`);
	return input instanceof HTMLInputElement ? input : null;
}

/**
* Responsibility:
* - Write a sidebar-requested jump into GF's native target-page field.
*
* Touches:
* - Mutates `gform_target_page_number_{formId}` immediately before submission.
*
* Guarantees:
* - Returns `true` when a native GF target-page field was updated.
*/
function writeNativeTargetPage(form, formId, targetPage) {
	const input = getNativeTargetPageInput(form, formId);
	if (!(input instanceof HTMLInputElement)) {
		return false;
	}

	input.value = String(toPositiveInteger(targetPage));
	return true;
}

/**
* Responsibility:
* - Register one native GF pre-submission filter for sidebar-triggered page jumps.
*
* Touches:
* - Hooks `gform/submission/pre_submission` through `gform.utils.addAsyncFilter`.
* - Copies pending sidebar target state into GF's native target-page hidden input when available.
*
* Guarantees:
* - Registration occurs at most once per form id.
* - Non-sidebar submissions keep Gravity Forms' default target-page behavior.
*/
export function registerSidebarPreSubmissionFilter(app) {
	const formId = Number(app?.config?.formId || 0);
	if (!Number.isFinite(formId) || formId <= 0) {
		return;
	}

	const registrationKey = String(formId);
	if (registeredPreSubmissionFilterForms.has(registrationKey)) {
		return;
	}

	const addAsyncFilter = window?.gform?.utils?.addAsyncFilter;
	if (typeof addAsyncFilter !== 'function') {
		app.logger.error('GF pre-submission hook unavailable: cannot register native sidebar step jump.', {
			formId,
			whatWentWrong: 'gform.utils.addAsyncFilter is not available in this runtime.',
			likelyCause: 'GF frontend script utilities did not initialize before configurator binding.',
			howToFix: 'Ensure GF scripts are loaded and bind configurator after `gform/theme/scripts_loaded`.',
		});
		return;
	}

	addAsyncFilter('gform/submission/pre_submission', async (data) => {
		const liveApp = getSidebarApp(formId) ?? app;
		const targetForm = data?.form;
		if (!(targetForm instanceof HTMLElement)) {
			return data;
		}

		const submittedFormId = getFormIdFromElement(targetForm);
		if (submittedFormId !== formId) {
			return data;
		}

		const requestedTargetPage = readRequestedTargetPage(targetForm);
		if (requestedTargetPage <= 0) {
			return data;
		}

		const sourcePage = resolveCurrentPage(liveApp, targetForm);
		if (sourcePage <= 0 || requestedTargetPage === sourcePage) {
			clearRequestedTargetPage(targetForm);
			return data;
		}

		const isForwardJump = requestedTargetPage > sourcePage;
		const isBackwardJump = requestedTargetPage < sourcePage;
		if ((isForwardJump && !isNextSubmissionType(data)) || (isBackwardJump && !isPreviousSubmissionType(data))) {
			clearRequestedTargetPage(targetForm);
			return data;
		}

		const nativeTargetWasUpdated = writeNativeTargetPage(targetForm, formId, requestedTargetPage);
		if (!nativeTargetWasUpdated) {
			liveApp.logger.debug('Sidebar step jump will rely on the custom requested-target field because the native GF target field is unavailable.', {
				formId,
				sourcePage,
				targetPage: requestedTargetPage,
				submissionType: String(data?.submissionType || ''),
			});
			return data;
		}

		liveApp.logger.debug('Sidebar step jump prepared native GF target page.', {
			formId,
			sourcePage,
			targetPage: requestedTargetPage,
			submissionType: String(data?.submissionType || ''),
		});

		return data;
	});

	registeredPreSubmissionFilterForms.add(registrationKey);
}

/**
* Responsibility:
* - Resolve previous button element from a GF page container.
*
* Touches:
* - Queries `.gform_previous_button` in provided page node.
*
* Guarantees:
* - Returns button/input element or `null`.
*/
function getPreviousButton(page) {
	if (!(page instanceof HTMLElement)) {
		return null;
	}

	const previousButton = page.querySelector('.gform_previous_button');
	return previousButton instanceof HTMLButtonElement || previousButton instanceof HTMLInputElement
		? previousButton
		: null;
}

/**
* Responsibility:
* - Resolve next button element from a GF page container.
*
* Touches:
* - Queries `.gform_next_button` in provided page node.
*
* Guarantees:
* - Returns button/input element or `null`.
*/
function getNextButton(page) {
	if (!(page instanceof HTMLElement)) {
		return null;
	}

	const nextButton = page.querySelector('.gform_next_button');
	return nextButton instanceof HTMLButtonElement || nextButton instanceof HTMLInputElement
		? nextButton
		: null;
}

/**
* Responsibility:
* - Handle grouped sidebar navigation clicks using native GF previous/next transitions.
*
* Touches:
* - Resolves current form/page state and stores a requested target page for the pre-submission bridge.
*
* Guarantees:
* - Backward and forward jumps are handed off to GF's native page-target pipeline.
*/
export function handleStepNavigationButtonClick(app, event, button) {
	if (!(button instanceof HTMLButtonElement)) {
		return;
	}

	const form = getForm(app.config);
	if (!(form instanceof HTMLElement)) {
		return;
	}

	const formId = toPositiveInteger(app?.config?.formId || getFormIdFromElement(form));
	const currentPage = resolveCurrentPage(app, form);
	const maxVisitedPage = resolveMaxVisitedPage(formId, currentPage);
	const targetPage = toPositiveInteger(button.value);
	if (targetPage <= 0 || currentPage <= 0 || targetPage === currentPage) {
		return;
	}

	event.preventDefault();

	const visibleStepNumbers = resolveVisibleStepNumbers(form, formId);
	if (!visibleStepNumbers.includes(targetPage) || targetPage > maxVisitedPage) {
		clearRequestedTargetPage(form);
		return;
	}

	const currentPageElement = findPageElement(form, currentPage);
	const navigationButton = targetPage < currentPage
		? getPreviousButton(currentPageElement)
		: getNextButton(currentPageElement);
	if (!(navigationButton instanceof HTMLButtonElement || navigationButton instanceof HTMLInputElement) || navigationButton.disabled) {
		clearRequestedTargetPage(form);
		return;
	}

	writeRequestedTargetPage(form, targetPage);
	navigationButton.click();
}
